import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { fileUrl, euro } from "../../lib/api";
import { Field } from "./useAdminApi";
import { FileField } from "./FileField";

const NO_SONG = "none";

// Shared admin list + form for free colorings and paid packs
export const AssetsTab = ({ kind, themes, songs, call }) => {
  const isPack = kind === "packs";
  const empty = { title: "", description: "", theme: "", song_id: NO_SONG, price: "4.90", preview_path: "", pdf_path: "", published: true, position: 0 };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const load = () => call("get", `/admin/${kind}`).then(setItems).catch(() => {});
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (item) => {
    setEditing(item);
    setF(item ? { ...empty, ...item, song_id: item.song_id || NO_SONG, price: item.price != null ? String(item.price) : empty.price } : { ...empty, theme: themes[0]?.slug || "" });
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    const body = { title: f.title, theme: f.theme, preview_path: f.preview_path, pdf_path: f.pdf_path, published: f.published, position: Number(f.position) || 0 };
    if (isPack) Object.assign(body, { description: f.description, price: Number(String(f.price).replace(",", ".")) });
    else body.song_id = f.song_id === NO_SONG ? null : f.song_id;
    setSaving(true);
    try {
      await call(editing ? "put" : "post", editing ? `/admin/${kind}/${editing.id}` : `/admin/${kind}`, body);
      toast.success("Enregistré"); setOpen(false); load();
    } catch { /* toast shown */ } finally { setSaving(false); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Supprimer « ${item.title} » ?`)) return;
    try { await call("delete", `/admin/${kind}/${item.id}`); } catch { return; }
    toast.success("Supprimé"); load();
  };
  const previewSrc = (item) => fileUrl(`/files/${kind}/${item.id}/preview`);
  const themeName = (slug) => themes.find((t) => t.slug === slug)?.name || slug;

  return (
    <div>
      <Button onClick={() => edit(null)} className="rounded-full bg-[#4CC417] hover:bg-[#3fae10]" data-testid={`admin-add-${kind}-btn`}>
        <Plus className="mr-1 h-4 w-4" /> {isPack ? "Ajouter un pack" : "Ajouter un coloriage"}
      </Button>
      <ul className="mt-5 space-y-3" data-testid={`admin-${kind}-list`}>
        {items.length === 0 && <li className="text-sm opacity-70">Rien pour l'instant.</li>}
        {items.map((it) => (
          <li key={it.id} className="flex items-center gap-3 rounded-[20px] bg-white p-3 shadow-sm" data-testid={`admin-${kind}-item-${it.id}`}>
            <img src={previewSrc(it)} alt="" className="h-16 w-14 shrink-0 rounded-xl bg-[#FFF9F2] object-contain" />
            <div className="min-w-0 flex-1">
              <p className="font-bold leading-tight break-words">{it.title}</p>
              <div className="mt-1 flex flex-wrap gap-2 text-xs font-semibold">
                <span className="rounded-full bg-[#FFF9F2] px-2 py-1">{themeName(it.theme)}</span>
                {isPack && <span className="rounded-full bg-[#8A3FE0] px-2 py-1 text-white">{euro(it.price)}</span>}
                {!isPack && it.song_id && <span className="rounded-full bg-[#38B6FF]/20 px-2 py-1">{songs.find((s) => s.id === it.song_id)?.title || "Comptine"}</span>}
                {!it.published && <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-1"><EyeOff className="h-3 w-3" /> Brouillon</span>}
              </div>
            </div>
            <Button variant="ghost" size="icon" aria-label="Modifier" onClick={() => edit(it)} data-testid={`admin-edit-${kind}-${it.id}`}><Pencil className="h-5 w-5" /></Button>
            <Button variant="ghost" size="icon" aria-label="Supprimer" onClick={() => remove(it)} data-testid={`admin-delete-${kind}-${it.id}`}><Trash2 className="h-5 w-5 text-[#F0303A]" /></Button>
          </li>
        ))}
      </ul>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto rounded-[28px] sm:max-w-xl" data-testid={`admin-${kind}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? "Modifier" : isPack ? "Nouveau pack" : "Nouveau coloriage"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Titre"><Input required value={f.title} onChange={(e) => set("title")(e.target.value)} data-testid={`${kind}-form-title`} /></Field>
            {isPack && <Field label="Description"><Textarea rows={3} value={f.description} onChange={(e) => set("description")(e.target.value)} data-testid="packs-form-description" /></Field>}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Thème">
                <Select value={f.theme} onValueChange={set("theme")}>
                  <SelectTrigger data-testid={`${kind}-form-theme`}><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{themes.map((t) => <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              {isPack ? (
                <Field label="Prix (€ TTC)"><Input required inputMode="decimal" value={f.price} onChange={(e) => set("price")(e.target.value)} data-testid="packs-form-price" /></Field>
              ) : (
                <Field label="Comptine associée">
                  <Select value={f.song_id} onValueChange={set("song_id")}>
                    <SelectTrigger data-testid="colorings-form-song"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_SONG}>Aucune</SelectItem>
                      {songs.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            <Field label="Image d'aperçu"><FileField kind="image" value={f.preview_path} onChange={set("preview_path")} call={call} testId={`${kind}-form-preview-input`} /></Field>
            <Field label={isPack ? "PDF du pack (envoyé après paiement)" : "PDF à télécharger"}><FileField kind="pdf" value={f.pdf_path} onChange={set("pdf_path")} call={call} testId={`${kind}-form-pdf-input`} /></Field>
            <div className="flex flex-wrap items-end gap-6">
              <Field label="Ordre"><Input type="number" className="w-24" value={f.position} onChange={(e) => set("position")(e.target.value)} data-testid={`${kind}-form-position`} /></Field>
              <label className="flex items-center gap-2 pb-2 font-semibold"><Switch checked={f.published} onCheckedChange={set("published")} data-testid={`${kind}-form-published`} /> Publié</label>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving || !f.theme || !f.preview_path || !f.pdf_path} className="rounded-full bg-[#38B6FF] hover:bg-[#1fa3ee]" data-testid={`${kind}-form-save`}>Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
